/*
  Product image zoom for the main product gallery (same behaviour as the Nile Rugs product page):
  - Desktop (mouse): a lens on the photo and a zoomed panel to the right of it
  - Desktop: clicking the photo opens a full-screen white lightbox with thumbnails
  - Mobile: pinch to zoom the photo in place
  - Mobile: tapping the photo opens a full-screen viewer (swipe, pinch / double-tap to zoom, tap to close)
  (the mobile photo slider with dots lives in aljus-mobile-gallery.js)
*/
(function () {
  const ZOOM = 2.5;
  const RESULT_SIZE = 420;
  const MAX_PINCH_SCALE = 4;
  const HOST_SELECTOR = 'product-info[data-product-zoom] media-gallery';
  const IMAGE_SELECTOR = '.product__media-item .product__media img';

  const lensMq = window.matchMedia('(min-width: 750px) and (hover: hover)');
  const desktopMq = window.matchMedia('(min-width: 750px)');

  // product-info.js calls this after a variant change; event delegation below already covers new markup
  if (typeof window.enableZoomOnHover !== 'function') window.enableZoomOnHover = function () {};

  function getHost(element) {
    return element && element.closest ? element.closest(HOST_SELECTOR) : null;
  }

  function getGalleryImage(target) {
    if (!target || !target.closest || !getHost(target)) return null;
    return target.closest(IMAGE_SELECTOR);
  }

  function largeSrc(image) {
    return image.getAttribute('src') || image.currentSrc;
  }

  function sizedSrc(image, width) {
    return largeSrc(image).replace(/width=\d+/, 'width=' + width);
  }

  /* ---- Desktop: lens + result panel ---- */
  const lens = document.createElement('div');
  lens.className = 'pz-lens';
  const result = document.createElement('div');
  result.className = 'pz-result';
  let activeHost = null;

  // actual rendered rect of the photo inside its frame (object-fit: contain letterboxing)
  function getRenderedRect(image, frame) {
    const naturalWidth = image.naturalWidth || frame.width;
    const naturalHeight = image.naturalHeight || frame.height;
    const scale = Math.min(frame.width / naturalWidth, frame.height / naturalHeight);
    const width = naturalWidth * scale;
    const height = naturalHeight * scale;
    return { w: width, h: height, x: (frame.width - width) / 2, y: (frame.height - height) / 2 };
  }

  function attach(host) {
    if (activeHost === host) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.appendChild(lens);
    host.appendChild(result);
    activeHost = host;
  }

  function hideZoom() {
    lens.style.display = 'none';
    result.style.display = 'none';
    if (activeHost) activeHost.classList.remove('pz-zooming');
  }

  document.addEventListener('mousemove', (event) => {
    const image = getGalleryImage(event.target);
    if (!image || !lensMq.matches) {
      if (lens.style.display === 'block') hideZoom();
      return;
    }

    const host = getHost(image);
    attach(host);
    host.classList.add('pz-zooming');

    const frame = image.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const rendered = getRenderedRect(image, frame);
    const imageLeft = frame.left - hostRect.left + rendered.x;
    const imageTop = frame.top - hostRect.top + rendered.y;

    // lens size in rendered-photo space, top-left clamped to the photo
    const lensWidth = Math.min(RESULT_SIZE / ZOOM, rendered.w);
    const lensHeight = Math.min(RESULT_SIZE / ZOOM, rendered.h);
    const cursorX = event.clientX - frame.left - rendered.x;
    const cursorY = event.clientY - frame.top - rendered.y;
    const lensX = Math.max(0, Math.min(rendered.w - lensWidth, cursorX - lensWidth / 2));
    const lensY = Math.max(0, Math.min(rendered.h - lensHeight, cursorY - lensHeight / 2));

    lens.style.left = imageLeft + lensX + 'px';
    lens.style.top = imageTop + lensY + 'px';
    lens.style.width = lensWidth + 'px';
    lens.style.height = lensHeight + 'px';
    lens.style.display = 'block';

    // result panel sits just to the right of the visible photo
    result.style.left = imageLeft + rendered.w + 8 + 'px';
    result.style.top = imageTop + 'px';
    result.style.width = lensWidth * ZOOM + 'px';
    result.style.height = lensHeight * ZOOM + 'px';
    result.style.backgroundImage = 'url("' + largeSrc(image) + '")';
    result.style.backgroundSize = rendered.w * ZOOM + 'px ' + rendered.h * ZOOM + 'px';
    result.style.backgroundPosition = -lensX * ZOOM + 'px ' + -lensY * ZOOM + 'px';
    result.style.display = 'block';
  });

  document.addEventListener('mouseout', (event) => {
    if (getGalleryImage(event.target) && !getGalleryImage(event.relatedTarget)) hideZoom();
  });

  /* ---- Lightbox ---- */
  const closeLabel =
    (document.querySelector('product-info[data-product-zoom]') || document.body).dataset.closeLabel || 'Close';
  const chevron = (path) =>
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="' +
    path +
    '" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const lightbox = document.createElement('div');
  lightbox.className = 'pz-lightbox';
  lightbox.setAttribute('role', 'dialog');
  lightbox.setAttribute('aria-modal', 'true');
  lightbox.innerHTML =
    '<div class="pz-lightbox__bd"></div>' +
    '<button type="button" class="pz-lightbox__close" aria-label="' +
    closeLabel +
    '">&#215;</button>' +
    '<button type="button" class="pz-lightbox__arrow pz-lightbox__arrow--prev" aria-label="Previous">' +
    chevron('M9 2L4 7l5 5') +
    '</button>' +
    '<button type="button" class="pz-lightbox__arrow pz-lightbox__arrow--next" aria-label="Next">' +
    chevron('M5 2l5 5-5 5') +
    '</button>' +
    '<div class="pz-lightbox__inner">' +
    '<div class="pz-lightbox__main"><img class="pz-lightbox__img" alt=""></div>' +
    '<div class="pz-lightbox__thumbs"></div>' +
    '</div>';

  const lightboxClose = lightbox.querySelector('.pz-lightbox__close');
  const lightboxThumbs = lightbox.querySelector('.pz-lightbox__thumbs');
  const lightboxMain = lightbox.querySelector('.pz-lightbox__main');
  const lightboxImg = lightbox.querySelector('.pz-lightbox__img');
  let images = [];
  let thumbs = [];
  let currentIdx = 0;
  let lastFocus = null;
  let changeTimer = null;

  function setActive(index) {
    currentIdx = (index + images.length) % images.length;
    const image = images[currentIdx];

    // quick fade between photos
    lightboxImg.classList.add('pz-lightbox__img--changing');
    clearTimeout(changeTimer);
    changeTimer = setTimeout(() => {
      lightboxImg.src = image.large;
      lightboxImg.alt = image.alt;
      lightboxImg.classList.remove('pz-lightbox__img--changing');
    }, 130);

    thumbs.forEach((thumb, j) => thumb.classList.toggle('pz-lightbox__thumb--active', j === currentIdx));
    if (thumbs[currentIdx]) thumbs[currentIdx].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  function openLightbox(clickedImage) {
    const galleryImages = Array.from(getHost(clickedImage).querySelectorAll(IMAGE_SELECTOR));
    images = galleryImages.map((image) => ({
      large: largeSrc(image),
      thumb: sizedSrc(image, 200),
      alt: image.alt || '',
    }));

    lightboxThumbs.innerHTML = '';
    thumbs = images.map((image, i) => {
      const thumb = document.createElement('img');
      thumb.src = image.thumb;
      thumb.alt = image.alt;
      thumb.className = 'pz-lightbox__thumb';
      thumb.setAttribute('role', 'button');
      thumb.tabIndex = 0;
      thumb.addEventListener('click', () => setActive(i));
      thumb.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          setActive(i);
        }
      });
      lightboxThumbs.appendChild(thumb);
      return thumb;
    });
    lightbox.classList.toggle('pz-lightbox--single', images.length < 2);

    if (!lightbox.isConnected) document.body.appendChild(lightbox);
    hideZoom();
    lastFocus = document.activeElement;
    lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    setActive(Math.max(0, galleryImages.indexOf(clickedImage)));
    lightboxClose.focus();
  }

  function closeLightbox() {
    lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocus) lastFocus.focus();
    lastFocus = null;
  }

  lightboxClose.addEventListener('click', closeLightbox);
  lightbox.querySelector('.pz-lightbox__bd').addEventListener('click', closeLightbox);
  lightbox.querySelector('.pz-lightbox__arrow--prev').addEventListener('click', () => setActive(currentIdx - 1));
  lightbox.querySelector('.pz-lightbox__arrow--next').addEventListener('click', () => setActive(currentIdx + 1));
  // clicking the empty area around the photo closes, like the backdrop
  lightboxMain.addEventListener('click', (event) => {
    if (event.target === lightboxMain) closeLightbox();
  });

  document.addEventListener('click', (event) => {
    const image = getGalleryImage(event.target);
    if (!image) return;
    event.preventDefault();
    if (desktopMq.matches) openLightbox(image);
    else openViewer(image);
  });

  document.addEventListener('keydown', (event) => {
    if (!lightbox.classList.contains('is-open')) return;
    if (event.key === 'Escape') return closeLightbox();
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') setActive(currentIdx - 1);
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') setActive(currentIdx + 1);
    if (event.key === 'Tab') {
      const focusable = Array.from(lightbox.querySelectorAll('button, [role="button"][tabindex="0"]'));
      if (focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  // swipe between photos inside the lightbox (touch screens at desktop width, e.g. tablets)
  let swipeStartX = 0;
  let swipeStartY = 0;
  lightboxMain.addEventListener(
    'touchstart',
    (event) => {
      swipeStartX = event.changedTouches[0].clientX;
      swipeStartY = event.changedTouches[0].clientY;
    },
    { passive: true }
  );
  lightboxMain.addEventListener(
    'touchend',
    (event) => {
      const dx = event.changedTouches[0].clientX - swipeStartX;
      const dy = event.changedTouches[0].clientY - swipeStartY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      setActive(dx < 0 ? currentIdx + 1 : currentIdx - 1);
    },
    { passive: true }
  );

  /* ---- Mobile: pinch to zoom in place ---- */
  let pinching = false;
  let startDistance = 0;
  let startScale = 1;
  let scale = 1;
  let zoomedImage = null;

  function getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function resetPinch() {
    scale = 1;
    pinching = false;
    if (zoomedImage) {
      zoomedImage.style.transform = '';
      zoomedImage.style.transformOrigin = '';
      zoomedImage = null;
    }
  }

  document.addEventListener(
    'touchstart',
    (event) => {
      if (desktopMq.matches || event.touches.length !== 2) return;
      const image = getGalleryImage(event.target);
      if (!image) return;
      event.preventDefault();
      if (zoomedImage && zoomedImage !== image) resetPinch();
      pinching = true;
      startDistance = getTouchDistance(event.touches);
      startScale = scale;
      zoomedImage = image;
    },
    { passive: false }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!pinching || event.touches.length < 2) return;
      event.preventDefault();
      scale = Math.min(MAX_PINCH_SCALE, Math.max(1, startScale * (getTouchDistance(event.touches) / startDistance)));
      zoomedImage.style.transform = 'scale(' + scale + ')';
      zoomedImage.style.transformOrigin = 'center center';
    },
    { passive: false }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!pinching || event.touches.length >= 2) return;
      pinching = false;
      if (scale < 1.05) resetPinch();
    },
    { passive: true }
  );

  // swiping to another slide resets the zoom
  document.addEventListener(
    'scroll',
    (event) => {
      if (!zoomedImage || pinching) return;
      // only the gallery slider scrolling counts, not the page scrolling
      if (event.target.nodeType === 1 && event.target.contains(zoomedImage)) resetPinch();
    },
    { capture: true, passive: true }
  );

  /* ---- Mobile: full-screen viewer (tap a photo; swipe, pinch/double-tap to zoom, tap to close) ---- */
  const TAP_SCALE = 2.5;
  const viewer = document.createElement('div');
  viewer.className = 'pz-viewer';
  viewer.setAttribute('aria-hidden', 'true');
  viewer.innerHTML =
    '<button type="button" class="pz-viewer__close" aria-label="' +
    closeLabel +
    '"><svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg></button>' +
    '<div class="pz-viewer__track"></div>' +
    '<div class="pz-viewer__dots"></div>';

  const viewerTrack = viewer.querySelector('.pz-viewer__track');
  const viewerDots = viewer.querySelector('.pz-viewer__dots');
  let viewerSlides = [];
  let dots = [];

  // zoom / pan state, one active slide at a time
  let zoomScale = 1;
  let panX = 0;
  let panY = 0;
  let activeImg = null;
  let activeSlide = null;

  function setActiveDot(index) {
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  function currentSlideIndex() {
    const width = viewerTrack.clientWidth;
    if (!width) return 0;
    return Math.min(viewerSlides.length - 1, Math.max(0, Math.round(viewerTrack.scrollLeft / width)));
  }

  function setGesturing(on) {
    if (activeImg) activeImg.classList.toggle('is-gesturing', on);
  }

  function applyTransform() {
    if (activeImg) activeImg.style.transform = 'translate(' + panX + 'px, ' + panY + 'px) scale(' + zoomScale + ')';
    viewerTrack.classList.toggle('is-zoomed', zoomScale > 1.01);
  }

  function clampPan() {
    if (!activeImg || !activeSlide) return;
    const maxPanX = Math.max(0, (activeImg.offsetWidth * zoomScale - activeSlide.clientWidth) / 2);
    const maxPanY = Math.max(0, (activeImg.offsetHeight * zoomScale - activeSlide.clientHeight) / 2);
    panX = Math.min(maxPanX, Math.max(-maxPanX, panX));
    panY = Math.min(maxPanY, Math.max(-maxPanY, panY));
  }

  function resetZoomState() {
    zoomScale = 1;
    panX = 0;
    panY = 0;
    if (activeImg) {
      setGesturing(false);
      activeImg.style.transform = '';
    }
    activeImg = null;
    activeSlide = null;
    viewerTrack.classList.remove('is-zoomed');
  }

  function toggleZoomAt(slide, clientX, clientY) {
    activeSlide = slide;
    activeImg = slide.querySelector('img');
    if (!activeImg) return;
    setGesturing(false);
    if (zoomScale > 1.01) return resetZoomState();
    zoomScale = TAP_SCALE;
    const rect = slide.getBoundingClientRect();
    panX = -(clientX - rect.left - rect.width / 2) * (zoomScale - 1);
    panY = -(clientY - rect.top - rect.height / 2) * (zoomScale - 1);
    clampPan();
    applyTransform();
  }

  // the Shopify chat bubble lives inside nested shadow roots, so plain CSS can't hide it
  function findChatHost(root) {
    if (!root) return null;
    const button = root.querySelector('[part="activator"], .activator, [aria-label="Chat"]');
    if (button) {
      const rootNode = button.getRootNode();
      return rootNode && rootNode.host ? rootNode.host : null;
    }
    for (const element of root.querySelectorAll('*')) {
      if (element.shadowRoot) {
        const found = findChatHost(element.shadowRoot);
        if (found) return found;
      }
    }
    return null;
  }

  function setChatHidden(hidden) {
    const host = findChatHost(document);
    if (!host) return;
    if (hidden) host.style.setProperty('display', 'none', 'important');
    else host.style.removeProperty('display');
  }

  function openViewer(clickedImage) {
    resetPinch();
    const galleryImages = Array.from(getHost(clickedImage).querySelectorAll(IMAGE_SELECTOR));

    viewerTrack.innerHTML = '';
    viewerDots.innerHTML = '';
    viewerSlides = galleryImages.map((image) => {
      const slide = document.createElement('div');
      slide.className = 'pz-viewer__slide';
      const img = document.createElement('img');
      img.src = largeSrc(image);
      img.alt = image.alt || '';
      slide.appendChild(img);
      viewerTrack.appendChild(slide);
      return slide;
    });
    dots = galleryImages.map((image, i) => {
      const dot = document.createElement('span');
      dot.className = 'pz-viewer__dot';
      dot.addEventListener('click', (event) => {
        event.stopPropagation();
        viewerTrack.scrollLeft = i * viewerTrack.clientWidth;
      });
      viewerDots.appendChild(dot);
      return dot;
    });
    viewerDots.hidden = galleryImages.length < 2;

    if (!viewer.isConnected) document.body.appendChild(viewer);
    resetZoomState();
    viewer.classList.add('is-open');
    viewer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setChatHidden(true);

    const index = Math.max(0, galleryImages.indexOf(clickedImage));
    viewerTrack.scrollLeft = index * viewerTrack.clientWidth;
    setActiveDot(index);
  }

  function closeViewer() {
    resetZoomState();
    viewer.classList.remove('is-open');
    viewer.setAttribute('aria-hidden', 'true');
    setChatHidden(false);
    document.body.style.overflow = '';
  }

  viewer.querySelector('.pz-viewer__close').addEventListener('click', (event) => {
    event.stopPropagation();
    closeViewer();
  });

  viewerTrack.addEventListener(
    'scroll',
    () => {
      if (zoomScale > 1.01) resetZoomState();
      setActiveDot(currentSlideIndex());
    },
    { passive: true }
  );

  window.addEventListener('resize', () => {
    if (!viewer.classList.contains('is-open')) return;
    const activeIndex = dots.findIndex((dot) => dot.classList.contains('is-active'));
    viewerTrack.scrollLeft = Math.max(0, activeIndex) * viewerTrack.clientWidth;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && viewer.classList.contains('is-open')) closeViewer();
  });

  // touch gestures: pinch to zoom, pan when zoomed, double-tap to zoom, single tap to close
  function touchMid(touches) {
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  const gesture = { mode: null, startDist: 0, startScale: 1, startPanX: 0, startPanY: 0, startX: 0, startY: 0, startMidX: 0, startMidY: 0 };
  let lastTapTime = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  let pendingTapTimer = null;
  let movedBeyondTapThreshold = false;

  viewerTrack.addEventListener(
    'touchstart',
    (event) => {
      const slide = event.target.closest('.pz-viewer__slide');
      if (!slide) return;

      if (event.touches.length === 2) {
        event.preventDefault();
        clearTimeout(pendingTapTimer);
        pendingTapTimer = null;
        activeSlide = slide;
        activeImg = slide.querySelector('img');
        setGesturing(true);
        const mid = touchMid(event.touches);
        Object.assign(gesture, {
          mode: 'pinch',
          startDist: getTouchDistance(event.touches),
          startScale: zoomScale,
          startPanX: panX,
          startPanY: panY,
          startMidX: mid.x,
          startMidY: mid.y,
        });
        return;
      }

      if (event.touches.length === 1) {
        // don't decide pan vs tap yet: touchmove pans when zoomed, touchend treats no movement as a tap
        const x = event.touches[0].clientX;
        const y = event.touches[0].clientY;
        // a possible second tap of a double-tap: stop the browser zooming the whole page
        if (Date.now() - lastTapTime < 300 && Math.abs(x - lastTapX) < 30 && Math.abs(y - lastTapY) < 30) {
          event.preventDefault();
        }
        activeSlide = slide;
        activeImg = slide.querySelector('img');
        Object.assign(gesture, { mode: 'touch1', startX: x, startY: y, startPanX: panX, startPanY: panY });
        movedBeyondTapThreshold = false;
      }
    },
    { passive: false }
  );

  viewerTrack.addEventListener(
    'touchmove',
    (event) => {
      if (gesture.mode === 'pinch' && event.touches.length === 2) {
        event.preventDefault();
        const mid = touchMid(event.touches);
        zoomScale = Math.min(
          MAX_PINCH_SCALE,
          Math.max(1, gesture.startScale * (getTouchDistance(event.touches) / gesture.startDist))
        );
        panX = gesture.startPanX + (mid.x - gesture.startMidX);
        panY = gesture.startPanY + (mid.y - gesture.startMidY);
        clampPan();
        applyTransform();
        return;
      }
      if (gesture.mode === 'touch1' && event.touches.length === 1) {
        const dx = event.touches[0].clientX - gesture.startX;
        const dy = event.touches[0].clientY - gesture.startY;
        if (Math.abs(dx) > 10 || Math.abs(dy) > 10) movedBeyondTapThreshold = true;
        if (zoomScale > 1.01) {
          event.preventDefault();
          setGesturing(true);
          panX = gesture.startPanX + dx;
          panY = gesture.startPanY + dy;
          clampPan();
          applyTransform();
        }
      }
    },
    { passive: false }
  );

  function endGesture(event) {
    if (gesture.mode === 'pinch' && event.touches.length === 1) {
      // carry on as a pan with the remaining finger
      Object.assign(gesture, {
        mode: 'touch1',
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        startPanX: panX,
        startPanY: panY,
      });
      movedBeyondTapThreshold = true;
      return;
    }
    if (event.touches.length > 0) return;

    if (gesture.mode === 'pinch' || movedBeyondTapThreshold) {
      setGesturing(false);
      if (zoomScale <= 1.02) resetZoomState();
      gesture.mode = null;
      return;
    }

    const slide = event.target.closest('.pz-viewer__slide');
    if (gesture.mode !== 'touch1' || !slide) return;
    if (event.cancelable) event.preventDefault(); // swallow native double-tap zoom and ghost click

    const now = Date.now();
    const isDoubleTap =
      now - lastTapTime < 300 && Math.abs(gesture.startX - lastTapX) < 30 && Math.abs(gesture.startY - lastTapY) < 30;
    if (isDoubleTap) {
      clearTimeout(pendingTapTimer);
      pendingTapTimer = null;
      lastTapTime = 0;
      toggleZoomAt(slide, gesture.startX, gesture.startY);
    } else {
      lastTapTime = now;
      lastTapX = gesture.startX;
      lastTapY = gesture.startY;
      pendingTapTimer = setTimeout(() => {
        pendingTapTimer = null;
        if (zoomScale <= 1.01) closeViewer();
      }, 300);
    }
  }

  viewerTrack.addEventListener('touchend', endGesture, { passive: false });
  viewerTrack.addEventListener('touchcancel', endGesture, { passive: false });

  /* ---- Desktop thumbnail row: round arrows, only shown when there is more to scroll to ---- */
  function updateThumbArrows(list) {
    const maxScroll = list.scrollWidth - list.clientWidth;
    list.pzPrev.hidden = list.scrollLeft <= 2;
    list.pzNext.hidden = list.scrollLeft >= maxScroll - 2;
  }

  function createThumbArrow(list, direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-thumb-arrow pz-thumb-arrow--' + direction;
    button.setAttribute('aria-label', direction === 'prev' ? 'Previous' : 'Next');
    button.innerHTML = chevron(direction === 'prev' ? 'M9 2L4 7l5 5' : 'M5 2l5 5-5 5');
    button.addEventListener('click', () => {
      const step = list.clientWidth * 0.8;
      list.scrollBy({ left: direction === 'prev' ? -step : step, behavior: 'smooth' });
    });
    list.parentElement.appendChild(button);
    return button;
  }

  function setupThumbArrows() {
    document.querySelectorAll(HOST_SELECTOR + ' .thumbnail-slider .thumbnail-list').forEach((list) => {
      if (!list.pzPrev) {
        list.pzPrev = createThumbArrow(list, 'prev');
        list.pzNext = createThumbArrow(list, 'next');
        list.addEventListener('scroll', () => updateThumbArrows(list), { passive: true });
      }
      updateThumbArrows(list);
    });
  }

  setupThumbArrows();
  window.addEventListener('resize', setupThumbArrows);
  // thumbnail images can change the row width once they load
  window.addEventListener('load', setupThumbArrows);
})();
